"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseTableFilterStateToDataGridFilter = exports.buildFilterStateFromUIDataGridFilter = exports.parseSeparatedValues = void 0;
const ui_components_1 = require("@groupby/ui-components");
const date_utils_1 = require("./table-converters/date-utils");
const number_utils_1 = require("./table-converters/number.utils");
const table_config_1 = require("../../store/shared/table-config");
/**
 * Parses a delimited string into an array of unique, trimmed values.
 * Supports multiple delimiters (comma and/or whitespace by default).
 * Empty values after trimming are filtered out.
 * Used specifically for Product ID "Is Any Of" operator.
 *
 * @param input - The input string potentially containing delimited values
 * @param delimiters - Array of delimiter strings to split by (default: [',', ' '])
 * @returns Array of unique, trimmed, non-empty values
 */
const parseSeparatedValues = (input, delimiters = [',', ' ']) => {
    if (!input || typeof input !== 'string') {
        return [];
    }
    // Create a regex pattern that matches any of the delimiters
    // Escape special regex characters in delimiters
    const escapedDelimiters = delimiters.map((d) => d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(escapedDelimiters.join('|'), 'g');
    const values = input
        .split(pattern)
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
    // Deduplicate using Set
    return Array.from(new Set(values));
};
exports.parseSeparatedValues = parseSeparatedValues;
const operatorToMatchOptionMap = {
    [ui_components_1.TextFilterOperatorTypes.Contains]: ui_components_1.MatchOptions.Include,
    [ui_components_1.TextFilterOperatorTypes.DoesNotContain]: ui_components_1.MatchOptions.Exclude,
    [ui_components_1.TextFilterOperatorTypes.IsAnyOf]: ui_components_1.MatchOptions.Include,
    [ui_components_1.TextFilterOperatorTypes.NotEquals]: ui_components_1.MatchOptions.Exclude,
};
const areInSameUtcDay = (leftTs, rightTs) => {
    const leftDate = new Date(leftTs);
    const rightDate = new Date(rightTs);
    return leftDate.getUTCFullYear() === rightDate.getUTCFullYear()
        && leftDate.getUTCMonth() === rightDate.getUTCMonth()
        && leftDate.getUTCDate() === rightDate.getUTCDate();
};
const isUtcSingleDayRange = (startAt, endAt) => areInSameUtcDay(startAt, endAt);
const isDateOperator = (operator) => (Object.values(ui_components_1.DateFilterOperatorTypes).includes(operator));
const isNumberOperator = (operator) => (Object.values(ui_components_1.NumberFilterOperatorTypes).includes(operator));
const buildListColumnFilter = (operator, value) => {
    if (operator === ui_components_1.ListFilterOperatorTypes.HasEvery) {
        if (Array.isArray(value)) {
            return value.length > 0
                ? {
                    type: table_config_1.TableColumnFilterTypes.MultiSelect,
                    list: value,
                    operator,
                }
                : null;
        }
        return null;
    }
    if (operator === ui_components_1.ListFilterOperatorTypes.IsAnyOf) {
        if ((0, ui_components_1.isSelectAllFilterValue)(value)) {
            return {
                type: table_config_1.TableColumnFilterTypes.MultiSelect,
                list: [],
                selectAll: true,
                excludedValues: value.excludedValues,
            };
        }
        if (Array.isArray(value)) {
            return value.length > 0 ? { type: table_config_1.TableColumnFilterTypes.MultiSelect, list: value } : null;
        }
        return undefined;
    }
    if (operator === ui_components_1.ListFilterOperatorTypes.IsNot) {
        if ((0, ui_components_1.isSelectAllFilterValue)(value)) {
            // UI emits "all except excludedValues"; Is Not inverts that into only the excluded values.
            const unselectedValues = value.excludedValues;
            return unselectedValues.length > 0
                ? { type: table_config_1.TableColumnFilterTypes.MultiSelect, list: unselectedValues }
                : {
                    type: table_config_1.TableColumnFilterTypes.MultiSelect,
                    list: [],
                    selectAll: true,
                    excludedValues: [],
                    operator,
                };
        }
        if (Array.isArray(value)) {
            return value.length > 0
                ? {
                    type: table_config_1.TableColumnFilterTypes.MultiSelect,
                    list: [],
                    excludedValues: value,
                    operator,
                }
                : null;
        }
        return null;
    }
    return undefined;
};
const buildColumnFilter = (operator, value) => {
    const numberFilter = (0, number_utils_1.buildNumberFilterValueFromUIDataGridFilter)(operator, value);
    if (numberFilter) {
        return Object.assign({ type: table_config_1.TableColumnFilterTypes.Number }, numberFilter);
    }
    const dateFilter = (0, date_utils_1.buildDateFilterValueFromUIDataGridFilter)(operator, value);
    if (dateFilter) {
        return dateFilter;
    }
    // If a date/number operator was provided but value could not be parsed,
    // skip this filter instead of falling back to a text filter.
    if (isDateOperator(operator) || isNumberOperator(operator)) {
        return null;
    }
    const listFilter = buildListColumnFilter(operator, value);
    if (listFilter !== undefined) {
        return listFilter;
    }
    if (value) {
        const match = (operatorToMatchOptionMap[operator] || ui_components_1.MatchOptions.Include);
        return {
            type: table_config_1.TableColumnFilterTypes.Text, operator, search: value, match,
        };
    }
    return null;
};
const getDateFilterTimestamp = (value) => {
    if (value == null)
        return undefined;
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? undefined : timestamp;
};
const hasStartDateOnly = (filter) => filter.type === table_config_1.TableColumnFilterTypes.Date
    && filter.startAt != null
    && filter.endAt == null;
const hasEndDateOnly = (filter) => filter.type === table_config_1.TableColumnFilterTypes.Date
    && filter.startAt == null
    && filter.endAt != null;
const mergeDateRangeFilterItems = (filterItems, logicOperator) => {
    if (logicOperator === 'or') {
        return filterItems;
    }
    const candidatesByField = filterItems.reduce((acc, item) => {
        var _a;
        const { field, filter } = item;
        if (!hasStartDateOnly(filter) && !hasEndDateOnly(filter)) {
            return acc;
        }
        const candidate = (_a = acc.get(field)) !== null && _a !== void 0 ? _a : { count: 0 };
        acc.set(field, Object.assign(Object.assign(Object.assign(Object.assign({}, candidate), { count: candidate.count + 1 }), hasStartDateOnly(filter) && { startItem: item }), hasEndDateOnly(filter) && { endItem: item }));
        return acc;
    }, new Map());
    const mergedItemsByField = new Map();
    const itemsToMerge = new Set();
    candidatesByField.forEach(({ count, startItem, endItem }, field) => {
        const startTimestamp = getDateFilterTimestamp(startItem === null || startItem === void 0 ? void 0 : startItem.filter.startAt);
        const endTimestamp = getDateFilterTimestamp(endItem === null || endItem === void 0 ? void 0 : endItem.filter.endAt);
        if (count !== 2 || !startItem || !endItem || startTimestamp == null || endTimestamp == null || startTimestamp > endTimestamp) {
            return;
        }
        mergedItemsByField.set(field, {
            field,
            filter: {
                type: table_config_1.TableColumnFilterTypes.Date,
                startAt: startItem.filter.startAt,
                endAt: endItem.filter.endAt,
            },
        });
        itemsToMerge.add(startItem);
        itemsToMerge.add(endItem);
    });
    const insertedMergedFields = new Set();
    return filterItems.reduce((acc, item) => {
        if (!itemsToMerge.has(item)) {
            acc.push(item);
            return acc;
        }
        const mergedItem = mergedItemsByField.get(item.field);
        if (mergedItem && !insertedMergedFields.has(item.field)) {
            acc.push(mergedItem);
            insertedMergedFields.add(item.field);
        }
        return acc;
    }, []);
};
/** Converts the UI grid filter model into the internal TableFilter format. */
const buildFilterStateFromUIDataGridFilter = (filter) => {
    const { items, logicOperator, } = filter;
    const filterItems = [];
    for (const item of items) {
        const columnFilter = buildColumnFilter(item.operator, item.value);
        if (columnFilter) {
            filterItems.push({ field: item.field, filter: columnFilter });
        }
    }
    const tableLogicOperator = String(logicOperator) === 'or' ? 'or' : 'and';
    return {
        items: mergeDateRangeFilterItems(filterItems, tableLogicOperator),
        logicOperator: tableLogicOperator,
    };
};
exports.buildFilterStateFromUIDataGridFilter = buildFilterStateFromUIDataGridFilter;
const matchOptionToOperatorMap = {
    [ui_components_1.MatchOptions.Include]: ui_components_1.TextFilterOperatorTypes.Contains,
    [ui_components_1.MatchOptions.Exclude]: ui_components_1.TextFilterOperatorTypes.DoesNotContain,
};
const buildDateGridFilterItems = (field, itemId, filter) => {
    const [startAt, endAt] = (0, date_utils_1.buildDataGridDateFilterValue)(filter);
    if (startAt != null && endAt == null) {
        return [{
                id: itemId,
                field,
                operator: ui_components_1.DateFilterOperatorTypes.After,
                value: new Date(startAt).toISOString(),
            }];
    }
    if (startAt == null && endAt != null) {
        return [{
                id: itemId,
                field,
                operator: ui_components_1.DateFilterOperatorTypes.Before,
                value: new Date(endAt).toISOString(),
            }];
    }
    if (startAt != null && endAt != null && (startAt === endAt || isUtcSingleDayRange(startAt, endAt))) {
        return [{
                id: itemId,
                field,
                operator: ui_components_1.DateFilterOperatorTypes.Is,
                value: new Date(startAt).toISOString(),
            }];
    }
    return [{
            id: itemId,
            field,
            operator: ui_components_1.DateFilterOperatorTypes.Range,
            value: [startAt, endAt],
        }];
};
const parseTableFilterStateToDataGridFilter = (tableFilter, search = '') => {
    const filterModel = {
        items: [],
        quickFilterValues: [search],
        logicOperator: (tableFilter.logicOperator === 'or' ? 'or' : 'and'),
    };
    tableFilter.items.forEach(({ field, filter: columnFilter }, index) => {
        const { type, operator: storedOperator } = columnFilter, rest = __rest(columnFilter, ["type", "operator"]);
        const itemId = `${field}-${index}`;
        switch (type) {
            case table_config_1.TableColumnFilterTypes.Text:
                {
                    const { match, search } = rest;
                    const textFilter = {
                        id: itemId,
                        field,
                        // Use stored operator if available, otherwise map from match option
                        operator: storedOperator || matchOptionToOperatorMap[match],
                        value: search,
                    };
                    filterModel.items.push(textFilter);
                }
                break;
            case table_config_1.TableColumnFilterTypes.MultiSelect:
                {
                    const { list = [], selectAll: isSelectAll, excludedValues = [] } = rest;
                    const isExcludedValuesFilter = !isSelectAll && excludedValues.length > 0;
                    const isInvertedSelectAllFilter = isSelectAll && storedOperator === ui_components_1.ListFilterOperatorTypes.IsNot;
                    const multiselectFilter = {
                        id: itemId,
                        field,
                        operator: storedOperator === ui_components_1.ListFilterOperatorTypes.HasEvery
                            ? ui_components_1.ListFilterOperatorTypes.HasEvery
                            : isExcludedValuesFilter || isInvertedSelectAllFilter
                                ? ui_components_1.ListFilterOperatorTypes.IsNot
                                : ui_components_1.ListFilterOperatorTypes.IsAnyOf,
                        value: isExcludedValuesFilter
                            ? excludedValues
                            : isSelectAll
                                ? { selectAll: true, excludedValues }
                                : list,
                    };
                    filterModel.items.push(multiselectFilter);
                }
                break;
            case table_config_1.TableColumnFilterTypes.Date:
                filterModel.items.push(...buildDateGridFilterItems(field, itemId, rest));
                break;
            case table_config_1.TableColumnFilterTypes.Number:
                {
                    const numberFilterValue = (0, number_utils_1.buildNumberGridFilterValue)(storedOperator, rest);
                    const numberFilter = {
                        id: itemId,
                        field,
                        operator: storedOperator || ui_components_1.NumberFilterOperatorTypes.Between,
                        value: numberFilterValue,
                    };
                    filterModel.items.push(numberFilter);
                }
                break;
        }
    });
    return filterModel;
};
exports.parseTableFilterStateToDataGridFilter = parseTableFilterStateToDataGridFilter;
